import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

const sizes = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  full: "max-w-full mx-4",
};

const Modal = ({
  open,
  onClose,
  title,
  children,
  size = "md",
  showClose = true,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  className = "",
  footer,
}) => {
  const handleEsc = useCallback(
    (e) => {
      if (closeOnEsc && e.key === "Escape") {
        onClose();
      }
    },
    [closeOnEsc, onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [open, handleEsc]);

  if (!open) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70 animate-fade-in"
      onClick={handleOverlayClick}
    >
      <div
        className={clsx(
          "relative w-full bg-white dark:bg-dark-800 rounded-xl shadow-modal",
          "animate-slide-up",
          sizes[size],
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
      >
        {/* Header */}
        {(title || showClose) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-dark-200 dark:border-dark-700">
            {title && (
              <h2
                id="modal-title"
                className="text-lg font-semibold text-dark-900 dark:text-dark-100"
              >
                {title}
              </h2>
            )}
            {showClose && (
              <button
                onClick={onClose}
                className="p-1 rounded-lg text-dark-400 hover:text-dark-600 hover:bg-dark-100 dark:hover:text-dark-300 dark:hover:bg-dark-700 transition-colors"
                aria-label="Fechar"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-dark-200 dark:border-dark-700 bg-dark-50 dark:bg-dark-900/50 rounded-b-xl">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// Sub-components for flexible usage
Modal.Header = ({ children, className = "" }) => (
  <div
    className={clsx(
      "px-6 py-4 border-b border-dark-200 dark:border-dark-700",
      className
    )}
  >
    {children}
  </div>
);

Modal.Body = ({ children, className = "" }) => (
  <div className={clsx("px-6 py-4", className)}>{children}</div>
);

Modal.Footer = ({ children, className = "" }) => (
  <div
    className={clsx(
      "flex items-center justify-end gap-3 px-6 py-4 border-t border-dark-200 dark:border-dark-700 bg-dark-50 dark:bg-dark-900/50 rounded-b-xl",
      className
    )}
  >
    {children}
  </div>
);

export default Modal;
